import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { BS_THEME_CHANGE_EVENT } from '../../../util/colorScheme'

const FALLBACK_BRAND = { r: 51, g: 153, b: 255 }
/** Keep in sync with `login-1.scss` (`.login__layout--themed` / dark mode block). */
const PAGE_BG = { light: '#e8e9ef', dark: '#1e2229' }
const DPR_CAP = 2
/** Base half-spread (radians); per-arm variance makes scalene, odd-shaped wedges. */
const RAY_SPREAD_RAD = 0.043
const RAY_LEN_JITTER = 0.1
const RAY_SPREAD_JITTER = 0.32
/** Scales both arms: wider far “base” of the wedge on some sweeps (deterministic per segment). */
const RAY_BASE_WIDE_MUL_MAX = 0.88
/**
 * Exponent on `det01^exp` for mouth width: lower → high width **more often** (thicker bases more
 * common); higher → rare very wide mouths only.
 */
const RAY_BASE_WIDE_EXP = 0.78
/** ~35–40% of segments: extra-wide “trunk” pass (thick base + slightly longer). */
const TRUNK_RAY_WIDTH_THRESHOLD = 0.38
const TRUNK_SPREAD_MUL = 1.22
const TRUNK_LEN_MUL = 1.09
/**
 * "Far" reference in normalized [0,1] layout space (0,0) top-left, (1,1) bottom-right — just past
 * each region so the ray still reads from a side, not the middle.
 */
const SOURCE_FAR = [
  { x: -0.32, y: -0.3 },
  { x: 1.24, y: -0.3 },
  { x: -0.32, y: 1.22 },
  { x: 1.24, y: 1.22 },
]
/**
 * Apex lerp from view center toward `SOURCE_FAR[zone]`; higher = tip sits farther toward the rim
 * (longer body before the wide base).
 */
const SOURCE_PULL = 0.72
const EDGE_T_PAD = 0.1
/** Ray length in multiples of max(w,h); drives how far the “base” of the wedge sits from the apex. */
const RAY_LEN_FACTOR = 2.52
/** How fast the pulse sweeps the ray (one full 0→1 per 1/TRAVEL_HZ seconds, then the source switches). */
const TRAVEL_HZ = 0.12
/**
 * `phase` = `elapsed * TRAVEL_HZ` + per-load offset. Without the offset, every visit starts at
 * `segment === 0`, `travel === 0` and `det01` is identical — same “seed” on each refresh.
 */
const INITIAL_PHASE_OFFSET_SPAN = 1e4
/** Skip heavy canvas (rays + audit dots / bursts) at or below this width — mobile / small tablets. */
const LOGIN_RAYS_DISABLE_MAX_WIDTH_PX = 767
const PULSE_SIGMA = 0.082
const GRADIENT_STOPS = 32
/**
 * Extra "before first pixel in view" in normalized ray param [0,1] so the pulse
 * center stays beyond the edge at travel=0 (Gaussian tail only until it sweeps in).
 */
const PULSE_LEAD = 0.28
const PULSE_T_END = 0.98
/**
 * First part of each sweep: global intensity 0 → 1 so the ray reads as off-screen, then
 * phasing in (avoids a visible "spawn" in-frame even when geometry already clips the wedge).
 */
const SPAWN_PHASE_END = 0.36

/**
 * Same geometry as compliance audit legend (`LegendValue.module.scss`: first span 5×5px).
 * Colors from `Assessment.jsx` tokens: `--aug-status-partial`, `--aug-status-noncompliant`.
 */
const AUDIT_LEGEND_DOT_DIAMETER_PX = 5
const AUDIT_LEGEND_DOT_RADIUS_PX = AUDIT_LEGEND_DOT_DIAMETER_PX / 2
/**
 * Hit test uses `travel` nudged forward so pops align slightly **before** the painted pulse peak
 * (same sweep, feels less “late”).
 */
const BEAM_HIT_TRAVEL_AHEAD = 0.078
/** Beam must be this “lit” at closest point on the ray segment (lower = easier pop). */
const BEAM_HIT_ALPHA_MIN = 0.088
const BEAM_VIS_MIN = 0.12
/** Extra px beyond geometric wedge half-width (keep tiny—large values pop targets outside the glow). */
const BEAM_PERP_SLACK_PX = 3
const BURST_LIFETIME_S = 2.85
const BURST_SPEED_MIN = 28
const BURST_SPEED_MAX = 92
/** Lower = glyphs stay on one character longer (easier to read). ~0.14 ≈ one step every ~7 frames @60Hz. */
const BURST_CHAR_FRAME_STEP = 0.14
/**
 * Burst lifetime → “toward compliance” curve: **lower** = hits full compliant **earlier** (less time in
 * yellow/red → mint mid-lerp that reads as teal).
 */
const BURST_TO_COMPLIANCE_EASE_POWER = 1.32
/** Slight per-particle desync so the shift to green doesn’t look perfectly simultaneous. */
const BURST_COLOR_STAGGER = 0.09
/** Applied to color lerp only: reaches full compliant RGB sooner than `blendT` (glyphs only; dots unchanged). */
const BURST_COMPLIANT_COLOR_ACCEL = 1.24
const BURST_COMPLIANT_COLOR_HEADROOM = 0.1
/**
 * After this fraction of burst lifetime (± `BURST_DOT_PHASE_STAGGER`), each particle is drawn as
 * the same compliant audit dot as the legend (`AUDIT_LEGEND_DOT_*` + `--aug-status-compliant`).
 */
const BURST_GLYPH_TO_DOT_LIFE_T = 0.7
const BURST_DOT_PHASE_STAGGER = 0.055
/** Lifetime opacity curve; < 1 keeps particles brighter longer (glyphs were reading ~40–60% opaque). */
const BURST_ALPHA_FADE_EXP = 0.78
/** Scales `complianceVis` in the alpha boost term. */
const BURST_COMPLIANT_ALPHA_MULT = 1.15
/** Added in dot phase so compliant circles stay solid. */
const BURST_DOT_ALPHA_PAD = 0.42
/**
 * When computed alpha is low, blend partway toward opaque so the wispy parts aren’t quite so faint.
 * `final = min(1, base + this * (1 - base))`.
 */
const BURST_ALPHA_LOW_OPACITY_LIFT = 0.16
/**
 * Floor on opacity while glyphs are shifting toward green (`blendT`), so late glyphs aren’t ghostly.
 */
const BURST_GLYPH_COMPLIANT_ALPHA_FLOOR = 0.72
/** Dark mode: skip dark stroke once this green — stroke was dulling the fill against the ray. */
const BURST_DARK_FILL_ONLY_BLEND_T = 0.42
const TECH_CHARS = ['#', '|', '-', '<', '>', '%', '@', '+']

/** Konami (↑↑↓↓←→←→BA): show audit dots + burst FX; denser spawns than the default hidden state. */
const KONAMI_KEYS = [
  'ArrowUp',
  'ArrowUp',
  'ArrowDown',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'ArrowLeft',
  'ArrowRight',
  'b',
  'a',
]
/** Konami-unlocked: denser floating dots + larger radial bursts than the former default animation. */
const EGG_TARGET_SPAWN_MS_MIN = 700
const EGG_TARGET_SPAWN_MS_MAX = 3800
const EGG_TARGET_FIRST_SPAWN_DELAY_MS_MIN = 280
const EGG_TARGET_FIRST_SPAWN_DELAY_MS_MAX = 1400
const EGG_BURST_PARTICLE_COUNT = 56

/**
 * Eased ramp 0 at travel=0 to 1 by `SPAWN_PHASE_END` (slower at the start = dimmer longer).
 * @param {number} travel
 */
function travelPhaseIn(travel) {
  if (travel >= SPAWN_PHASE_END) {
    return 1
  }
  if (travel <= 0) {
    return 0
  }
  const u = travel / SPAWN_PHASE_END
  return u ** 2.4
}

/**
 * @returns {number|null} distance in px along unit direction to first point inside the closed rect, or null
 */
function firstDistanceIntoView(ox, oy, ux, uy, minX, minY, maxX, maxY) {
  const EPS = 1e-9
  const o = [ox, oy]
  const d = [ux, uy]
  const b0 = [minX, minY]
  const b1 = [maxX, maxY]
  let t0 = 0
  let t1 = 1e30
  for (let a = 0; a < 2; a += 1) {
    if (Math.abs(d[a]) < EPS) {
      if (o[a] < b0[a] || o[a] > b1[a]) {
        return null
      }
    } else {
      const invD = 1 / d[a]
      const ta0 = (b0[a] - o[a]) * invD
      const ta1 = (b1[a] - o[a]) * invD
      const tSmall = Math.min(ta0, ta1)
      const tBig = Math.max(ta0, ta1)
      t0 = Math.max(t0, tSmall)
      t1 = Math.min(t1, tBig)
    }
  }
  if (t0 > t1) {
    return null
  }
  if (t0 < 0) {
    // Origin inside: still use forward segment into the box; treat as 0
    t0 = 0
  }
  return t0
}

/** Deterministic 0..1 for segment-scoped variation (no flicker). */
function det01(n) {
  const x = Math.sin(n * 12.9898) * 43758.5453
  return x - Math.floor(x)
}

/**
 * @param {number} n
 * @param {number} a
 * @param {number} b
 */
function detInRange(n, a, b) {
  return a + (b - a) * det01(n)
}

/**
 * Apex in normalized (0,1) extended space: lerp from center toward `SOURCE_FAR[sourceIndex]`.
 * @param {number} sourceIndex
 */
function sourceApexNorm(sourceIndex) {
  const { x: fx, y: fy } = SOURCE_FAR[sourceIndex]
  return {
    nx: 0.5 + SOURCE_PULL * (fx - 0.5),
    ny: 0.5 + SOURCE_PULL * (fy - 0.5),
  }
}

/**
 * A point on the **opposite** side of the view from the source zone (not the far corner), with margin.
 * Cycles by `sourceIndex` which edge family is used; `segment` picks position along the edge.
 * @param {number} w
 * @param {number} h
 * @param {number} sourceIndex
 * @param {number} segment
 */
function oppositeEdgeTarget(w, h, sourceIndex, segment) {
  const t1 = detInRange(segment * 19 + sourceIndex * 3, EDGE_T_PAD, 1 - EDGE_T_PAD)
  const t2 = detInRange(segment * 29 + sourceIndex * 5, EDGE_T_PAD, 1 - EDGE_T_PAD)
  const useFirst = det01(segment * 7 + 11) < 0.5
  // 0 = TL, 1 = TR, 2 = BL, 3 = BR — opposite = bottom/right, bottom/left, top/right, top/left
  if (sourceIndex === 0) {
    if (useFirst) {
      return { x: t1 * w, y: h }
    }
    return { x: w, y: t2 * h }
  }
  if (sourceIndex === 1) {
    if (useFirst) {
      return { x: t1 * w, y: h }
    }
    return { x: 0, y: t2 * h }
  }
  if (sourceIndex === 2) {
    if (useFirst) {
      return { x: t1 * w, y: 0 }
    }
    return { x: w, y: t2 * h }
  }
  if (useFirst) {
    return { x: t1 * w, y: 0 }
  }
  return { x: 0, y: t2 * h }
}

function parseCssRgb(input) {
  if (!input) {
    return null
  }
  const t = String(input).trim()
  const hash = t.match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i)
  if (hash) {
    let h = hash[1]
    if (h.length === 3) {
      h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2]
    }
    const n = parseInt(h, 16)
    if (!Number.isNaN(n)) {
      return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 }
    }
  }
  const rgb = t.match(/rgba?\(\s*([0-9.]+)\s*,\s*([0-9.]+)\s*,\s*([0-9.]+)/i)
  if (rgb) {
    return { r: Math.round(+rgb[1]), g: Math.round(+rgb[2]), b: Math.round(+rgb[3]) }
  }
  return null
}

/**
 * @param {{ r: number, g: number, b: number }} a
 * @param {{ r: number, g: number, b: number }} b
 * @param {number} t
 */
function lerpRgb(a, b, t) {
  const u = Math.max(0, Math.min(1, t))
  return {
    r: Math.round(a.r + (b.r - a.r) * u),
    g: Math.round(a.g + (b.g - a.g) * u),
    b: Math.round(a.b + (b.b - a.b) * u),
  }
}

/**
 * @param {{ r: number, g: number, b: number }} rgb
 */
function rgbToCss(rgb) {
  return `rgb(${rgb.r},${rgb.g},${rgb.b})`
}

function getBrandFromDom() {
  if (typeof document === 'undefined') {
    return FALLBACK_BRAND
  }
  const s = getComputedStyle(document.documentElement)
  const aug = s.getPropertyValue('--aug-color-brand').trim()
  if (aug) {
    const p = parseCssRgb(aug)
    if (p) {
      return p
    }
  }
  const brgb = s.getPropertyValue('--bs-primary-rgb').trim()
  if (brgb) {
    const parts = brgb.split(/[\s,]+/).map((n) => parseInt(n, 10))
    if (parts.length >= 3 && parts.every((n) => !Number.isNaN(n))) {
      return { r: parts[0], g: parts[1], b: parts[2] }
    }
  }
  const bsP = s.getPropertyValue('--bs-primary').trim()
  if (bsP) {
    const p = parseCssRgb(bsP)
    if (p) {
      return p
    }
  }
  return FALLBACK_BRAND
}

const AUDIT_COLOR_PARTIAL_FALLBACK = '#ffc000'
const AUDIT_COLOR_NONCOMPLIANT_FALLBACK = '#ff5c5c'
const AUDIT_COLOR_COMPLIANT_FALLBACK = '#66ca86'

const DEFAULT_AUDIT_LEGEND_COLORS = {
  partial: AUDIT_COLOR_PARTIAL_FALLBACK,
  nonCompliant: AUDIT_COLOR_NONCOMPLIANT_FALLBACK,
  compliant: AUDIT_COLOR_COMPLIANT_FALLBACK,
}

/**
 * Resolved colors for audit legend dots: partial, not compliant, compliant (theme tokens).
 */
function readAuditComplianceLegendColors() {
  if (typeof document === 'undefined') {
    return DEFAULT_AUDIT_LEGEND_COLORS
  }
  const s = getComputedStyle(document.documentElement)
  const partial = s.getPropertyValue('--aug-status-partial').trim() || AUDIT_COLOR_PARTIAL_FALLBACK
  const nonCompliant = s.getPropertyValue('--aug-status-noncompliant').trim() || AUDIT_COLOR_NONCOMPLIANT_FALLBACK
  const compliant = s.getPropertyValue('--aug-status-compliant').trim() || AUDIT_COLOR_COMPLIANT_FALLBACK
  return { partial, nonCompliant, compliant }
}

/**
 * Gaussian along the open segment; `tPeak` is the center (may be < 0 so the bulge is still
 * mostly off the drawn triangle until it moves in).
 */
function alphaAlongRay(t, tPeak) {
  const d = t - tPeak
  return Math.exp(-(d * d) / (2 * PULSE_SIGMA * PULSE_SIGMA))
}

/**
 * Fills a scalene wedge: two different arm lengths and spreads (odd triangle). Linear alpha
 * along `aimAngle` to `Lgrad` (max projection) so the pulse still reads along the “shot.”
 * Filling the canvas with PAGE_BG first avoids `source-over` stack-up.
 * @param {number} Lgrad — same basis used for `tEnter` / `tPeak` (0…1 = apex → along aim).
 */
function drawRay(ctx, { ox, oy, aimAngle, L1, L2, spread1, spread2, Lgrad, maxAlpha, tPeak, rgb }) {
  const a1 = aimAngle - spread1
  const a2 = aimAngle + spread2
  const x1 = ox + Math.cos(a1) * L1
  const y1 = oy + Math.sin(a1) * L1
  const x2 = ox + Math.cos(a2) * L2
  const y2 = oy + Math.sin(a2) * L2
  const ux = Math.cos(aimAngle)
  const uy = Math.sin(aimAngle)
  const g = ctx.createLinearGradient(ox, oy, ox + ux * Lgrad, oy + uy * Lgrad)
  const { r, g: gg, b } = rgb
  for (let s = 0; s <= GRADIENT_STOPS; s += 1) {
    const t = s / GRADIENT_STOPS
    const a = maxAlpha * alphaAlongRay(t, tPeak)
    g.addColorStop(t, `rgba(${r},${gg},${b},${a})`)
  }

  ctx.beginPath()
  ctx.moveTo(ox, oy)
  ctx.lineTo(x1, y1)
  ctx.lineTo(x2, y2)
  ctx.closePath()
  ctx.fillStyle = g
  ctx.fill()
}

/**
 * Closest point on segment apex → apex + (ux,uy)·segLen; (ux,uy) unit.
 * @returns {{ dist: number, alongClamped: number, tNorm: number }}
 */
function distanceToRaySegment(ox, oy, ux, uy, segLen, px, py) {
  const dx = px - ox
  const dy = py - oy
  const along = dx * ux + dy * uy
  const alongClamped = Math.max(0, Math.min(segLen, along))
  const qx = ox + ux * alongClamped
  const qy = oy + uy * alongClamped
  const dist = Math.hypot(px - qx, py - qy)
  const tNorm = alongClamped / Math.max(segLen, 1e-6)
  return { dist, alongClamped, tNorm }
}

const FORM_EXCLUSION_PAD_PX = 22
const SPAWN_MARGIN_PX = 28
/** White card under `#authForm` when present — tighter “inner square” than the whole column. */
const SPAWN_EXCLUSION_INNER_SELECTOR = '#authForm .login__wrapper'
const SPAWN_REJECTION_MAX_ATTEMPTS = 96

/**
 * Padded axis-aligned rect to avoid (canvas coords), from the login card / form column.
 * @param {DOMRect} containerRect
 * @param {number} w
 * @param {number} h
 */
function getSpawnExclusionRect(containerRect, w, h) {
  if (typeof document === 'undefined') {
    return {
      exL: w * 0.32,
      exT: h * 0.1,
      exR: w * 0.68,
      exB: h * 0.82,
    }
  }
  const inner = document.querySelector(SPAWN_EXCLUSION_INNER_SELECTOR) || document.getElementById('authForm')
  if (!inner) {
    return {
      exL: w * 0.32,
      exT: h * 0.1,
      exR: w * 0.68,
      exB: h * 0.82,
    }
  }
  const fr = inner.getBoundingClientRect()
  const p = FORM_EXCLUSION_PAD_PX
  const exL = Math.max(0, Math.min(w, fr.left - containerRect.left - p))
  const exT = Math.max(0, Math.min(h, fr.top - containerRect.top - p))
  const exR = Math.max(0, Math.min(w, fr.right - containerRect.left + p))
  const exB = Math.max(0, Math.min(h, fr.bottom - containerRect.top + p))
  return { exL, exT, exR, exB }
}

/**
 * True if (x,y) lies inside the closed exclusion rectangle (padded card / form).
 */
function isInsideSpawnExclusion(x, y, exL, exT, exR, exB) {
  return x >= exL && x <= exR && y >= exT && y <= exB
}

/**
 * Uniform random point in the canvas minus viewport margins and minus the inner card rect.
 */
function pickSpawnOutsideAuthForm(container, w, h) {
  const cr = container.getBoundingClientRect()
  const { exL, exT, exR, exB } = getSpawnExclusionRect(cr, w, h)
  const m = SPAWN_MARGIN_PX
  const innerW = w - 2 * m
  const innerH = h - 2 * m
  if (innerW < 24 || innerH < 24) {
    return { x: w * 0.5, y: m + 12 }
  }

  for (let attempt = 0; attempt < SPAWN_REJECTION_MAX_ATTEMPTS; attempt += 1) {
    const x = m + Math.random() * innerW
    const y = m + Math.random() * innerH
    if (!isInsideSpawnExclusion(x, y, exL, exT, exR, exB)) {
      return { x, y }
    }
  }

  const pickFromStrip = () => {
    const strips = []
    if (exT > m + 12) {
      strips.push(() => ({ x: m + Math.random() * innerW, y: m + Math.random() * (exT - m - 4) }))
    }
    if (h - exB > m + 12) {
      strips.push(() => ({ x: m + Math.random() * innerW, y: exB + 4 + Math.random() * (h - exB - m - 8) }))
    }
    if (exL > m + 12) {
      strips.push(() => ({ x: m + Math.random() * (exL - m - 4), y: m + Math.random() * innerH }))
    }
    if (w - exR > m + 12) {
      strips.push(() => ({ x: exR + 4 + Math.random() * (w - exR - m - 8), y: m + Math.random() * innerH }))
    }
    if (strips.length === 0) {
      return { x: m + Math.random() * innerW, y: m + Math.random() * innerH }
    }
    return strips[Math.floor(Math.random() * strips.length)]()
  }

  return pickFromStrip()
}

function getLoginRaysAnimationDisabled() {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return false
  }
  return (
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    window.matchMedia(`(max-width: ${LOGIN_RAYS_DISABLE_MAX_WIDTH_PX}px)`).matches
  )
}

/**
 * @param {KeyboardEvent} e
 * @returns {string}
 */
function konamiKeyFromEvent(e) {
  const { key } = e
  if (key.startsWith('Arrow')) {
    return key
  }
  if (key.length === 1) {
    return key.toLowerCase()
  }
  return key
}

export function LoginBackgroundAnimation({ className }) {
  const containerRef = useRef(null)
  const canvasRef = useRef(null)
  const rafIdRef = useRef(0)
  const startRef = useRef(0)
  const lastRafTimeRef = useRef(0)
  const colorCacheRef = useRef(FALLBACK_BRAND)
  const auditLegendColorsCacheRef = useRef(DEFAULT_AUDIT_LEGEND_COLORS)
  const lastColorAtRef = useRef(0)
  const frameRef = useRef(0)
  const nextEntityIdRef = useRef(1)
  const floatingTargetsRef = useRef([])
  const burstParticlesRef = useRef([])
  const nextSpawnAtRef = useRef(0)
  const konamiUnlockedRef = useRef(false)
  const konamiProgressRef = useRef(0)
  const dotsDestroyedCountRef = useRef(0)
  const [raysAnimationDisabled, setRaysAnimationDisabled] = useState(getLoginRaysAnimationDisabled)
  const [phaseOffset] = useState(() => (typeof window !== 'undefined' ? Math.random() * INITIAL_PHASE_OFFSET_SPAN : 0))

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return undefined
    }
    const mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)')
    const mqMobile = window.matchMedia(`(max-width: ${LOGIN_RAYS_DISABLE_MAX_WIDTH_PX}px)`)
    const sync = () => setRaysAnimationDisabled(mqReduce.matches || mqMobile.matches)
    sync()
    mqReduce.addEventListener('change', sync)
    mqMobile.addEventListener('change', sync)
    return () => {
      mqReduce.removeEventListener('change', sync)
      mqMobile.removeEventListener('change', sync)
    }
  }, [])

  useEffect(() => {
    if (raysAnimationDisabled || typeof window === 'undefined') {
      return undefined
    }
    const onKeyDown = (e) => {
      if (konamiUnlockedRef.current) {
        return
      }
      const k = konamiKeyFromEvent(e)
      const expected = KONAMI_KEYS[konamiProgressRef.current]
      if (k === expected) {
        konamiProgressRef.current += 1
        if (konamiProgressRef.current >= KONAMI_KEYS.length) {
          konamiUnlockedRef.current = true
          konamiProgressRef.current = 0
          dotsDestroyedCountRef.current = 0
          const now = typeof performance !== 'undefined' ? performance.now() : Date.now()
          nextSpawnAtRef.current = now + 380
        }
      } else {
        konamiProgressRef.current = k === KONAMI_KEYS[0] ? 1 : 0
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [raysAnimationDisabled])

  const tick = useCallback(
    (time) => {
      const canvas = canvasRef.current
      const container = containerRef.current
      if (!canvas || !container) {
        return
      }
      if (!startRef.current) {
        startRef.current = time
      }
      const elapsed = (time - startRef.current) * 0.001

      const w = container.clientWidth
      const h = container.clientHeight
      if (w < 1 || h < 1) {
        rafIdRef.current = requestAnimationFrame(tick)
        return
      }

      const dpr = Math.min(window.devicePixelRatio || 1, DPR_CAP)
      if (canvas.width !== Math.floor(w * dpr) || canvas.height !== Math.floor(h * dpr)) {
        canvas.width = Math.floor(w * dpr)
        canvas.height = Math.floor(h * dpr)
      }

      const ctx = canvas.getContext('2d')
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.clearRect(0, 0, w, h)

      const isDark = document.documentElement.getAttribute('data-bs-theme') === 'dark'
      // Opaque page fill so transparent canvas + semi-opaque layers don’t add up on the same pixels.
      ctx.fillStyle = isDark ? PAGE_BG.dark : PAGE_BG.light
      ctx.fillRect(0, 0, w, h)

      const maxApexAlpha = isDark ? 0.22 : 0.2
      frameRef.current += 1
      if (frameRef.current % 20 === 0 || time - lastColorAtRef.current > 2000) {
        colorCacheRef.current = getBrandFromDom()
        auditLegendColorsCacheRef.current = readAuditComplianceLegendColors()
        lastColorAtRef.current = time
      }
      const rgb = colorCacheRef.current

      const prevT = lastRafTimeRef.current || time
      const dtSec = Math.min(0.05, Math.max(0, (time - prevT) / 1000))
      lastRafTimeRef.current = time

      const dotsOn = konamiUnlockedRef.current

      if (dotsOn && nextSpawnAtRef.current === 0) {
        nextSpawnAtRef.current =
          time +
          EGG_TARGET_FIRST_SPAWN_DELAY_MS_MIN +
          Math.random() * (EGG_TARGET_FIRST_SPAWN_DELAY_MS_MAX - EGG_TARGET_FIRST_SPAWN_DELAY_MS_MIN)
      }
      if (dotsOn && time >= nextSpawnAtRef.current && w > 80 && h > 80) {
        const { x: sx, y: sy } = pickSpawnOutsideAuthForm(container, w, h)
        floatingTargetsRef.current.push({
          id: nextEntityIdRef.current,
          x: sx,
          y: sy,
          r: AUDIT_LEGEND_DOT_RADIUS_PX,
          variant: det01(nextEntityIdRef.current * 7.13) < 0.5 ? 'partial' : 'nonCompliant',
        })
        nextEntityIdRef.current += 1
        nextSpawnAtRef.current =
          time + EGG_TARGET_SPAWN_MS_MIN + Math.random() * (EGG_TARGET_SPAWN_MS_MAX - EGG_TARGET_SPAWN_MS_MIN)
      }

      const burstParticles = burstParticlesRef.current
      if (dotsOn) {
        for (let i = burstParticles.length - 1; i >= 0; i -= 1) {
          const p = burstParticles[i]
          const age = (time - p.bornAt) / 1000
          if (age >= BURST_LIFETIME_S) {
            burstParticles.splice(i, 1)
          } else {
            p.x += p.vx * dtSec
            p.y += p.vy * dtSec
            if (p.x < -80 || p.x > w + 80 || p.y < -80 || p.y > h + 80) {
              burstParticles.splice(i, 1)
            }
          }
        }
      } else {
        burstParticles.length = 0
      }

      const maxDim = Math.max(w, h)
      const Lbase = RAY_LEN_FACTOR * maxDim
      // One `segment` index → one zone + one `travel` in [0,1) (same floor, stays in sync).
      const phase = elapsed * TRAVEL_HZ + phaseOffset
      const segment = Math.floor(phase)
      const nZones = SOURCE_FAR.length
      const sourceIndex = segment % nZones
      const travel = phase - segment
      const { nx, ny } = sourceApexNorm(sourceIndex)
      const ox = nx * w
      const oy = ny * h
      const { x: tx, y: ty } = oppositeEdgeTarget(w, h, sourceIndex, segment)
      const aimAngle = Math.atan2(ty - oy, tx - ox)
      const ux = Math.cos(aimAngle)
      const uy = Math.sin(aimAngle)

      const nSeg = segment * 16.9 + sourceIndex * 4.7
      const baseL1 = Lbase * (1 - 0.5 * RAY_LEN_JITTER + RAY_LEN_JITTER * det01(nSeg))
      const baseL2 = Lbase * (1 - 0.5 * RAY_LEN_JITTER + RAY_LEN_JITTER * det01(nSeg + 2.1))
      // Wider mouth (far edge) on more sweeps; `RAY_BASE_WIDE_EXP` < 1 skews toward thick bases.
      const spreadMouthW = 1 + RAY_BASE_WIDE_MUL_MAX * det01(segment * 0.6 + 11) ** RAY_BASE_WIDE_EXP
      const isTrunkRay = det01(segment * 3.1 + 8.4) > 1 - TRUNK_RAY_WIDTH_THRESHOLD
      const trunkMul = isTrunkRay ? TRUNK_LEN_MUL : 1
      const L1 = baseL1 * trunkMul
      const L2 = baseL2 * trunkMul
      const spreadMouthTrunk = isTrunkRay ? TRUNK_SPREAD_MUL : 1
      const spread1 =
        RAY_SPREAD_RAD *
        spreadMouthW *
        spreadMouthTrunk *
        (1 - 0.5 * RAY_SPREAD_JITTER + RAY_SPREAD_JITTER * det01(nSeg + 3.3))
      const spread2 =
        RAY_SPREAD_RAD *
        spreadMouthW *
        spreadMouthTrunk *
        (1 - 0.5 * RAY_SPREAD_JITTER + RAY_SPREAD_JITTER * det01(nSeg + 5.1))
      // Furthest point along the aim: scalene, one arm can project farther
      const Lgrad = Math.max(L1 * Math.cos(spread1), L2 * Math.cos(spread2), 1)

      const sEnter = firstDistanceIntoView(ox, oy, ux, uy, 0, 0, w, h)
      let tEnter = 0.2
      if (sEnter != null && sEnter > 0 && Lgrad > 1) {
        tEnter = sEnter / Lgrad
        tEnter = Math.min(0.9, Math.max(0.02, tEnter))
      }
      // Pulse center starts *before* the first on-screen t so the glow comes in from the edge, not ~20% in
      const tFrom = tEnter - PULSE_LEAD
      const tPeak = tFrom + (PULSE_T_END - tFrom) * travel
      const vis = travelPhaseIn(travel)
      const travelHit = Math.min(1, travel + BEAM_HIT_TRAVEL_AHEAD)
      const tPeakHit = tFrom + (PULSE_T_END - tFrom) * travelHit
      const visHit = travelPhaseIn(travelHit)

      drawRay(ctx, {
        ox,
        oy,
        aimAngle,
        L1,
        L2,
        spread1,
        spread2,
        Lgrad,
        maxAlpha: maxApexAlpha * vis,
        tPeak,
        rgb,
      })

      const maxSpread = Math.max(spread1, spread2)
      const tanMax = Math.tan(maxSpread)

      const floatingTargets = floatingTargetsRef.current
      if (dotsOn) {
        for (let ti = floatingTargets.length - 1; ti >= 0; ti -= 1) {
          const tgt = floatingTargets[ti]
          const tr = tgt.r ?? AUDIT_LEGEND_DOT_RADIUS_PX
          const { dist, tNorm, alongClamped } = distanceToRaySegment(ox, oy, ux, uy, Lgrad, tgt.x, tgt.y)
          // Half-width of the wedge grows from the apex; don’t use full Lgrad (that let hits outside the glow).
          const halfWidthAtAlong = alongClamped * tanMax
          const maxPerpHit = halfWidthAtAlong * 0.9 + tr * 0.55 + BEAM_PERP_SLACK_PX
          const alphaAtClosest = maxApexAlpha * visHit * alphaAlongRay(Math.min(1, Math.max(0, tNorm)), tPeakHit)
          const beamTouches =
            dist < maxPerpHit &&
            tNorm >= -0.03 &&
            tNorm <= 1.04 &&
            alphaAtClosest >= BEAM_HIT_ALPHA_MIN &&
            visHit >= BEAM_VIS_MIN

          if (beamTouches) {
            const angleStep = (Math.PI * 2) / EGG_BURST_PARTICLE_COUNT
            for (let bi = 0; bi < EGG_BURST_PARTICLE_COUNT; bi += 1) {
              const jitter = det01(segment * 3.1 + bi * 7.7 + tgt.id) * 0.35
              const ang = bi * angleStep + jitter - 0.175
              const sp = BURST_SPEED_MIN + det01(segment * 5 + bi + tgt.id * 11) * (BURST_SPEED_MAX - BURST_SPEED_MIN)
              burstParticles.push({
                id: nextEntityIdRef.current,
                bornAt: time,
                x: tgt.x,
                y: tgt.y,
                vx: Math.cos(ang) * sp,
                vy: Math.sin(ang) * sp,
                charSeed: det01(tgt.id * 19.1 + bi * 13.7),
                sourceVariant: tgt.variant,
              })
              nextEntityIdRef.current += 1
            }
            dotsDestroyedCountRef.current += 1
            floatingTargets.splice(ti, 1)
          }
        }
      } else {
        floatingTargets.length = 0
      }

      if (!dotsOn) {
        rafIdRef.current = requestAnimationFrame(tick)
        return
      }

      ctx.save()
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      const auditDotColors = auditLegendColorsCacheRef.current
      for (let ti = 0; ti < floatingTargets.length; ti += 1) {
        const tgt = floatingTargets[ti]
        const tr = tgt.r ?? AUDIT_LEGEND_DOT_RADIUS_PX
        const fill = tgt.variant === 'nonCompliant' ? auditDotColors.nonCompliant : auditDotColors.partial
        ctx.beginPath()
        ctx.arc(tgt.x, tgt.y, tr, 0, Math.PI * 2)
        ctx.fillStyle = fill
        ctx.fill()
      }

      const frame = frameRef.current
      const burstFontPx = Math.min(18, Math.max(12, Math.round(13 + Math.min(w, h) * 0.011)))
      ctx.font = `600 ${burstFontPx}px Consolas, "Courier New", "Liberation Mono", monospace`
      ctx.lineWidth = 2.2
      const partialRgb = parseCssRgb(auditDotColors.partial) ??
        parseCssRgb(AUDIT_COLOR_PARTIAL_FALLBACK) ?? { r: 255, g: 192, b: 0 }
      const nonCompliantRgb = parseCssRgb(auditDotColors.nonCompliant) ??
        parseCssRgb(AUDIT_COLOR_NONCOMPLIANT_FALLBACK) ?? { r: 255, g: 92, b: 92 }
      const compliantRgb = parseCssRgb(auditDotColors.compliant) ??
        parseCssRgb(AUDIT_COLOR_COMPLIANT_FALLBACK) ?? { r: 102, g: 202, b: 134 }
      for (let bi = 0; bi < burstParticles.length; bi += 1) {
        const p = burstParticles[bi]
        const age = (time - p.bornAt) / 1000
        const lifeT = age / BURST_LIFETIME_S
        const dotPhaseAt = BURST_GLYPH_TO_DOT_LIFE_T + (p.charSeed - 0.5) * BURST_DOT_PHASE_STAGGER
        const isDot = lifeT >= dotPhaseAt
        const towardCompliance = 1 - (1 - lifeT) ** BURST_TO_COMPLIANCE_EASE_POWER
        const stagger = (p.charSeed - 0.5) * BURST_COLOR_STAGGER
        const blendT = Math.max(0, Math.min(1, towardCompliance + stagger))
        const blendColor = Math.min(1, blendT * BURST_COMPLIANT_COLOR_ACCEL + BURST_COMPLIANT_COLOR_HEADROOM)
        const fadeLife = (1 - lifeT) ** BURST_ALPHA_FADE_EXP
        const complianceVis = isDot ? 1 : Math.max(blendT, blendColor)
        const alphaBoosted = Math.min(
          1,
          fadeLife * (1 + BURST_COMPLIANT_ALPHA_MULT * complianceVis) + (isDot ? BURST_DOT_ALPHA_PAD : 0),
        )
        const alphaFloor = isDot ? 0 : blendT * blendT * BURST_GLYPH_COMPLIANT_ALPHA_FLOOR
        const alphaBase = Math.max(alphaBoosted, alphaFloor)
        const alphaLifted = Math.min(1, alphaBase + BURST_ALPHA_LOW_OPACITY_LIFT * (1 - alphaBase))
        ctx.globalAlpha = Math.max(0, alphaLifted)
        if (isDot) {
          const tr = AUDIT_LEGEND_DOT_RADIUS_PX
          ctx.beginPath()
          ctx.arc(p.x, p.y, tr, 0, Math.PI * 2)
          ctx.fillStyle = auditDotColors.compliant
          ctx.fill()
          continue
        }
        const startRgb = p.sourceVariant === 'nonCompliant' ? nonCompliantRgb : partialRgb
        const mixed = lerpRgb(startRgb, compliantRgb, blendColor)
        const fillCss = rgbToCss(mixed)
        const ch = TECH_CHARS[Math.floor(frame * BURST_CHAR_FRAME_STEP + p.charSeed * 23 + bi * 3) % TECH_CHARS.length]
        if (isDark) {
          ctx.fillStyle = fillCss
          ctx.fillText(ch, p.x, p.y)
          if (blendColor < BURST_DARK_FILL_ONLY_BLEND_T) {
            const strokeRgb = lerpRgb(mixed, { r: 8, g: 12, b: 20 }, 0.52)
            ctx.strokeStyle = rgbToCss(strokeRgb)
            ctx.strokeText(ch, p.x, p.y)
          }
        } else {
          ctx.fillStyle = fillCss
          ctx.fillText(ch, p.x, p.y)
        }
      }
      ctx.globalAlpha = 1
      ctx.restore()

      // Hand-drawn style HUD: running total of audit dots popped by the ray (Konami mode only).
      ctx.save()
      const destroyed = dotsDestroyedCountRef.current
      const hudLine = `Dots destroyed: ${destroyed}`
      const hudPx = Math.min(24, Math.max(15, Math.round(16 + Math.min(w, h) * 0.014)))
      ctx.font = `700 ${hudPx}px Consolas, "Courier New", "Liberation Mono", monospace`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'bottom'
      const hudX = w * 0.5
      const hudY = h - 22
      ctx.lineJoin = 'round'
      ctx.miterLimit = 2
      const outlineRgb = isDark ? '18,22,30' : '255,255,255'
      const fillRgb = isDark ? '232,236,245' : '35,40,58'
      ctx.lineWidth = Math.max(3, hudPx * 0.18)
      ctx.strokeStyle = `rgba(${outlineRgb},0.92)`
      ctx.strokeText(hudLine, hudX, hudY)
      ctx.lineWidth = Math.max(1.2, hudPx * 0.06)
      ctx.strokeStyle = `rgba(${outlineRgb},0.35)`
      ctx.strokeText(hudLine, hudX + 1, hudY + 1)
      ctx.fillStyle = `rgb(${fillRgb})`
      ctx.fillText(hudLine, hudX, hudY)
      ctx.restore()

      rafIdRef.current = requestAnimationFrame(tick)
    },
    [phaseOffset],
  )

  useEffect(() => {
    if (raysAnimationDisabled) {
      return undefined
    }
    rafIdRef.current = requestAnimationFrame(tick)
    const onVis = () => {
      cancelAnimationFrame(rafIdRef.current)
      if (document.visibilityState !== 'hidden') {
        rafIdRef.current = requestAnimationFrame(tick)
      }
    }
    const onTheme = () => {
      colorCacheRef.current = getBrandFromDom()
    }
    document.addEventListener('visibilitychange', onVis)
    if (typeof window !== 'undefined') {
      window.addEventListener(BS_THEME_CHANGE_EVENT, onTheme)
    }
    return () => {
      cancelAnimationFrame(rafIdRef.current)
      document.removeEventListener('visibilitychange', onVis)
      if (typeof window !== 'undefined') {
        window.removeEventListener(BS_THEME_CHANGE_EVENT, onTheme)
      }
    }
  }, [tick, raysAnimationDisabled])

  if (raysAnimationDisabled) {
    return null
  }

  return (
    <div className={className} ref={containerRef} aria-hidden>
      <canvas ref={canvasRef} className="login__background-rays__canvas" />
    </div>
  )
}

LoginBackgroundAnimation.propTypes = {
  className: PropTypes.string,
}

export default React.memo(LoginBackgroundAnimation)
